import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormNumberInput } from '../../../../../component/form/FormNumberInput';
import { FormDatePicker } from '../../../../../component/form/FormDatePicker';
import React from 'react';
import { UseFormSetValue, useWatch } from 'react-hook-form';
import RestHttpClient from '../../../../../common/RestHttpClient';
import { pushNotificationHandler } from '../../../../../common/PushNotification';
import { VinAssignmentField } from './VinField';
import { FinancesDrawerFormValues, VinConfigValues } from '../RightDrawerSchema';

interface FinancesObjectPropetyDetailsProps {
  control: any;
  onRelevantChange: (name: string, value: unknown) => void;
  setValue: UseFormSetValue<FinancesDrawerFormValues>;
  loading: boolean;
  readonly?: boolean;
  dealId?: number;
  modelId?: number;
  dealTypeId?: number;
  vehicleCondition?: number;
  hasSubmittedCaclulation?: boolean;
}

export const FinancesObjectPropetyDetails = ({
  control,
  onRelevantChange,
  loading,
  readonly,
  dealId,
  modelId,
  dealTypeId,
  vehicleCondition,
  setValue,
  hasSubmittedCaclulation,
}: FinancesObjectPropetyDetailsProps) => {
  const { t } = useTranslation();

  const handleRelevantBlurEvent = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onRelevantChange(event.target.name, event.target.value);
  };

  const handleRegistrationDateBlur = async (value: Date | undefined) => {
    const isValidRegistrationDate = await validateVehicleAge(value);
    if (!isValidRegistrationDate) {
      pushNotificationHandler.publish(
        t('deals.new.modelConfiguration.body.vehicleDetails.thisCarIsTooOldToDoCalculationWithIt'),
        'warning'
      );
    } else {
      onRelevantChange('objectConfig.registrationDate', value);
    }
  };

  const validateVehicleAge = (registrationDate: Date | undefined): Promise<boolean> => {
    if (registrationDate === undefined || registrationDate === null) {
      return Promise.resolve(true);
    }
    if (dealId !== undefined && dealId !== null) {
      return RestHttpClient.validateVehicleAgeByDealId(dealId!, registrationDate!).then((res) => {
        return res.data.isValid;
      });
    } else {
      if (modelId === undefined || vehicleCondition === undefined || dealTypeId === undefined) {
        return Promise.resolve(true);
      } else {
        return RestHttpClient.validateVehicleAge(
          modelId!,
          vehicleCondition!,
          dealTypeId!,
          registrationDate!
        ).then((res) => {
          return res.data.isValid;
        });
      }
    }
  };

  const regDate = useWatch({ control, name: 'objectConfig.registrationDate' });

  return (
    <Box sx={{ width: 1 / 2, display: 'flex', flexDirection: 'column', pr: 3, pl: 2 }}>
      <h3>{t('deals.new.financesConfiguration.calculationDrawer.objectConfig.propertyDetails')}</h3>
      <Box sx={{ m: 1 }}>
        {regDate !== undefined && (
          <FormDatePicker
            control={control}
            name="objectConfig.registrationDate"
            label={t(
              'deals.new.financesConfiguration.calculationDrawer.objectConfig.registrationDate'
            )}
            shrink
            disableUnderline
            disabled={readonly}
            onChange={(event) => handleRegistrationDateBlur(event.target.value)}
          />
        )}
      </Box>
      <FormNumberInput
        control={control}
        disabled={loading || readonly}
        sx={{ m: 1 }}
        name="objectConfig.initialMileage"
        label={t('deals.new.financesConfiguration.calculationDrawer.objectConfig.initialMileage')}
        shrink
        onBlur={handleRelevantBlurEvent}
      />
      {dealId !== undefined && (
        <VinAssignmentField
          control={control}
          handleRelevantChange={() => onRelevantChange('vinAssignments', null)}
          setValue={setValue as unknown as UseFormSetValue<{ vinConfigs: VinConfigValues[] }>}
          disabled={hasSubmittedCaclulation}
        />
      )}
    </Box>
  );
};
